import {
  showSuccess,
  showConfirm,
  showLoading,
  showCustom,
  showError,
  hideLoading,
} from "@/assets/js/utils/alertService";
import api from "@/assets/js/utils/api"; // ✅ 引入 axios 客戶端
import { DateHelper } from "@/assets/js/utils/time";

export default {
  name: "EmployeeManagement",

  data() {
    return {
      searchQuery: "",
      activeFilter: "all",
      showAddModal: false,
      showDetailModal: false,
      showResetModal: false,
      showPassword: false,
      editingEmployee: null,
      selectedEmployee: null,
      formData: {
        EmployeeCode: "",
        FullName: "",
        Account: "",
        Email: "",
        PhoneNumber: "",
        HireDate: "",
        Role: "一般員工",
        IsActive: true,
        Password: "",
      },

      employees: [],
      currentPage: 1,
      itemsPerPage: 5,

      resetCountdownMap: {}, // 🆕 用員工ID當索引記住各自倒數狀態

      // ✅ 角色類型定義（由高到低權限排序）
      roleTypes: {
        工程師: { label: "工程師", color: "#dc2626" }, // 紅色 - 最高權限
        平台總管: { label: "平台總管", color: "#9333ea" }, // 紫色
        高階主管: { label: "高階主管", color: "#f59e0b" }, // 橙色
        中階主管: { label: "中階主管", color: "#eab308" }, // 黃色
        一般員工: { label: "一般員工", color: "#3b82f6" }, // 藍色 - 最低權限
      },
    };
  },

  computed: {
    filters() {
      const roleStats = {};

      // 計算每個角色的人數
      Object.keys(this.roleTypes).forEach((role) => {
        roleStats[role] = this.employees.filter((e) => e.Role === role).length;
      });

      return [
        { label: "全部員工", value: "all", count: this.stats.total },
        { label: "在職", value: "active", count: this.stats.active },
        { label: "停用", value: "inactive", count: this.stats.inactive },
      ];
    },
    filteredEmployees() {
      let result = this.employees;

      // 搜尋
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          (emp) =>
            emp.EmployeeCode.toLowerCase().includes(query) ||
            emp.FullName.toLowerCase().includes(query) ||
            emp.Email.toLowerCase().includes(query) ||
            emp.PhoneNumber.includes(query) ||
            emp.Account.toLowerCase().includes(query),
        );
      }

      // 篩選
      if (this.activeFilter === "active") {
        result = result.filter((emp) => emp.IsActive);
      } else if (this.activeFilter === "inactive") {
        result = result.filter((emp) => !emp.IsActive);
      } else if (this.activeFilter === "engineer") {
        result = result.filter((emp) => emp.Role === "工程師");
      }

      return result;
    },

    paginatedEmployees() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredEmployees.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredEmployees.length / this.itemsPerPage);
    },

    stats() {
      return {
        total: this.employees.length,
        active: this.employees.filter((e) => e.IsActive).length,
        inactive: this.employees.filter((e) => !e.IsActive).length,
      };
    },

    // ✅ 編輯時可用的權限選項（不包括工程師）
    editableRoles() {
      return {
        平台總管: this.roleTypes["平台總管"],
        高階主管: this.roleTypes["高階主管"],
        中階主管: this.roleTypes["中階主管"],
        一般員工: this.roleTypes["一般員工"],
      };
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    activeFilter() {
      this.currentPage = 1;
    },
  },

  methods: {
    formatDate(dateString) {
      return DateHelper.toTaiwanFormat(dateString);
    },

    async fetchEmployees() {
      try {
        const res = await api.get("/employee/all"); // 呼叫後端 API
        this.employees = res.data.data;
      } catch (error) {
        // 這裡可不用 showError，因為攔截器已經顯示「登入逾時」
        console.warn("載入員工資料失敗：", error);
      }
    },
    handleSearch() {
      // 搜尋邏輯已在 computed 中處理
    },

    viewEmployeeDetail(employee) {
      this.selectedEmployee = employee;
      this.showDetailModal = true;
    },

    editEmployee(employee) {
      this.editingEmployee = employee;
      this.formData = {
        EmployeeCode: employee.EmployeeCode,
        FullName: employee.FullName,
        Account: employee.Account,
        Email: employee.Email,
        PhoneNumber: employee.PhoneNumber,
        HireDate: employee.HireDate,
        Role: employee.Role || "一般員工",
        IsActive: employee.IsActive,
        Password: "",
      };
      this.showAddModal = true;
      this.showDetailModal = false;
    },

    resetPassword(employee) {
      this.selectedEmployee = employee;
      this.showResetModal = true;
      if (this.resetCountdownMap[employee.Id] === undefined) {
        // 若沒紀錄 → 初始化
        this.resetCountdownMap[employee.Id] = 0; // 設定初始倒數
      }
    },

    async confirmResetPassword() {
      // 按下確認重設執行
      try {
        showLoading("寄送中，請稍候..."); // 顯示載入動畫

        const response = await api.post("/employee/send-reset-password", {
          email: this.selectedEmployee.Email, // 傳送該員工 Email
        });

        hideLoading(); // 隱藏載入動畫
        if (response.data.success) {
          showSuccess("重設密碼的郵件已成功發送！"); // 成功提示
          this.showResetModal = false; // 關閉 Modal

          const id = this.selectedEmployee.Id; // 取該員工ID
          this.resetCountdownMap[id] = 15; // 倒數15秒

          let timer = setInterval(() => {
            // 啟動計時器
            this.resetCountdownMap[id]--; // 每秒 -1

            if (this.resetCountdownMap[id] <= 0) {
              // 倒數結束
              clearInterval(timer); // 停止倒數
              this.resetCountdownMap[id] = 0; // 重置為0
            }
          }, 1000);
        } else {
          showError(response.data.message || "無法發送重置密碼郵件");
        }
      } catch (error) {
        hideLoading();
        showError(error.response?.data?.message || "發送重置郵件時發生錯誤");
      }
    },
    async deleteEmployee(code) {
      // 依 employeeCode 找資料
      const employee = this.employees.find((e) => e.EmployeeCode === code);

      // 如果找不到直接 return
      if (!employee) return;

      // ⚠️ SweetAlert 警告（保留原本）
      const confirmed = await showConfirm(
        `警告：刪除員工後將無法復原！\n\n確定要刪除員工「${employee.FullName}」嗎？`,
      );

      // 點取消就不做事
      if (!confirmed.isConfirmed) return;

      try {
        // ✅ 呼叫後端 API
        await api.delete(`/employee/delete/${employee.Id}`);

        showSuccess("成功刪除員工！");

        // ✅ 從前端陣列移除
        this.employees = this.employees.filter((e) => e.Id !== employee.Id);

        // ✅ 刪除後如果該頁沒資料 → 往前一頁
        if (this.paginatedEmployees.length === 0 && this.currentPage > 1) {
          this.currentPage--;
        }
      } catch (err) {
        const msg = err.response?.data?.message || "刪除失敗";
        showCustom({
          icon: "error",
          title: "錯誤",
          text: msg,
        });
      }
    },

    async saveEmployee() {
      // 按下儲存按鈕觸發
      try {
        // 使用 try/catch 捕捉錯誤
        if (this.editingEmployee) {
          // ✅ 編輯模式送給後端
          const payload = {
            employeeId: this.editingEmployee.Id, // 後端識別用
            fullName: this.formData.FullName,
            phoneNumber: this.formData.PhoneNumber,
            role: this.formData.Role,
            isActive: this.formData.IsActive,
          };

          // ✅ 呼叫 PUT API
          await api.put("/employee/update", payload);

          showSuccess(
            "更新成功！",
            `員工 ${this.formData.FullName} 資料已更新`,
          );

          // ✅ 前端同步更新
          const emp = this.employees.find(
            (e) => e.Id === this.editingEmployee.Id,
          );
          if (emp) {
            emp.FullName = this.formData.FullName;
            emp.PhoneNumber = this.formData.PhoneNumber;
            emp.Role = this.formData.Role;
            emp.IsActive = this.formData.IsActive;
            emp.UpdateDate = new Date().toISOString();
          }

          this.closeModal();
        } else {
          // 如果是新增
          const payload = {
            // 準備送給後端的資料
            employeeCode: this.formData.EmployeeCode, // 員工編號
            fullName: this.formData.FullName, // 員工姓名
            email: this.formData.Email, // 信箱
            phoneNumber: this.formData.PhoneNumber, // 電話
            account: this.formData.Account, //  後端需求 accout
            password: this.formData.Password, // 密碼
            role: this.formData.Role, // 角色
            managerId: null, // 主管 ID (假裝沒有)
          };

          await api.post("/employee/add", payload); // ✅ 送出 POST 請求 (串後端)

          showSuccess("員工建立成功！", "後端新增成功"); // 顯示成功提示
          this.fetchEmployees(); // 重新抓取員工資料
        }

        this.closeModal(); // 關閉 Modal 視窗
      } catch (error) {
        // 捕捉後端錯誤
        const msg = error.response?.data?.message || "發生未知錯誤"; // 取得後端錯誤訊息
        showCustom({
          icon: "error",
          title: "新增失敗",
          text: msg, // 顯示錯誤
        });
      }
    },
    generateNextEmployeeCode() {
      const prefix = "EMP";
      let maxNumber = 0;

      this.employees.forEach((emp) => {
        const match = emp.EmployeeCode?.match(/^EMP(\d+)$/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxNumber) maxNumber = num;
        }
      });

      const nextNumber = maxNumber + 1;
      return `${prefix}${nextNumber.toString().padStart(3, "0")}`; // 格式：EMP001
    },

    async toggleEmployeeStatus(employee) {
      const action = employee.IsActive ? "停用" : "啟用";
      const confirmResult = await showConfirm(
        `確定要${action}員工「${employee.FullName}」嗎？`,
      );

      if (!confirmResult.isConfirmed) return;

      try {
        // ✅ 呼叫後端共用更新 API（沿用編輯 API）
        await api.put("/employee/update", {
          employeeId: employee.Id, // 🔥 重點：用後端資料庫 Id
          fullName: employee.FullName,
          phoneNumber: employee.PhoneNumber,
          admin: employee.Admin,
          isActive: !employee.IsActive, // ✅ ✅ 反轉狀態
        });

        // ✅ 更新前端顯示
        employee.IsActive = !employee.IsActive;
        employee.UpdateDate = new Date().toLocaleString("zh-TW");

        showSuccess(
          `已${action}員工`,
          `員工 ${employee.FullName} 現在為「${
            employee.IsActive ? "在職" : "停用"
          }」狀態。`,
        );
      } catch (error) {
        const msg = error.response?.data?.message ?? "更新狀態失敗";
        showCustom({
          icon: "error",
          title: "錯誤",
          text: msg,
        });
      }
    },
    closeModal() {
      this.showAddModal = false;
      this.editingEmployee = null;
      this.formData = {
        EmployeeCode: "",
        FullName: "",
        Account: "",
        Email: "",
        PhoneNumber: "",
        HireDate: "",
        Admin: false,
        IsActive: true,
        Password: "",
      };
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
  },

  mounted() {
    this.fetchEmployees(); // ✅ 載入畫面就抓員工資料
  },
};
